import { useState } from 'react';
import Papa from 'papaparse';
import { css } from '@emotion/react';
import { Link } from '@material-ui/core';
import NavResponsiveModal, { ModalContent, ModalHeader } from '@/components/Navigation/NavResponsiveModal';
import { colors } from '@/styles';
import CSVDropzone from '@/components/CSVDropzone';
import { SKU_CSV_COLUMNS, buildSkuCsvTemplate, getSkuUpdateColumns, parseSkuCsvRows } from './utils';

// Bulk-create SKUs from a fixed-template CSV (no header mapping). Attachments
// can't live in a CSV, so this creates SKU rows only.
const SKUImportModal = ({ open, onClose, onImport, loading }) => {
    const [error, setError] = useState('');
    const [dropzoneKey, setDropzoneKey] = useState(0);
    const resetDropzone = () => setDropzoneKey((k) => k + 1);

    const handleFile = (file) => {
        setError('');
        Papa.parse(file, {
            header: true,
            skipEmptyLines: 'greedy',
            complete: (result) => {
                const rows = parseSkuCsvRows(result.data);
                if (!rows.length) {
                    setError('No valid rows found — each row needs at least a SKU.');
                    resetDropzone();
                    return;
                }
                const skipped = Math.max(0, (result.data?.length || 0) - rows.length);
                onImport(rows, skipped, getSkuUpdateColumns(result.meta?.fields));
            },
            error: () => {
                setError('Could not read the .csv file.');
                resetDropzone();
            },
        });
    };

    const downloadTemplate = () => {
        const blob = new Blob([buildSkuCsvTemplate()], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sku_template.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <NavResponsiveModal open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <ModalHeader title="Import CSV" onClose={onClose} />
            <ModalContent>
                <CSVDropzone
                    key={dropzoneKey}
                    accept={{ 'text/csv': ['.csv'] }}
                    label={loading ? 'Importing…' : 'Drag and Drop or Select'}
                    disabled={loading}
                    disableChanges={loading}
                    handleFileAdd={handleFile}
                />
                {error ? (
                    <div
                        css={css`
                            color: ${colors.reds.primary};
                            margin-top: 0.5rem;
                        `}
                    >
                        {error}
                    </div>
                ) : null}
                <div
                    css={css`
                        margin-top: 1rem;
                        font-size: 13px;
                    `}
                >
                    Expected columns: {SKU_CSV_COLUMNS.join(', ')}.{' '}
                    <Link component="button" type="button" onClick={downloadTemplate}>
                        Download template
                    </Link>
                </div>
            </ModalContent>
        </NavResponsiveModal>
    );
};

export default SKUImportModal;
